import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "../api/axiosInstance";
import { usePmsTheme } from "./PmsThemeContext";
import { useCurrencyDisplay } from "../currency/CurrencyDisplayContext";
import {
  currencyAmountStep,
  formatCurrencyAmount,
  getCurrencyMinorUnits,
  hasValidCurrencyPrecision,
} from "../currency/currencyPrecision";
import { readStoredUser } from "../utils/sessionUser";
import { DEMO_BUSINESS_UNIT_ID, isDemoMode, readDemoUser } from "../demo/demoMode";

const DEMO_CHARGES_MESSAGES = {
  en: {
    readOnly: "Demo · View only. Changes are disabled.",
    update: "This demo JSON does not include the complete charge catalog and upgrade matrix. Update the demo data and refresh to try again.",
    refresh: "Refresh", detail: "Charge type details", select: "Select a charge name to view its details.",
    currency: "Stored currency · No conversion", order: "Stored category order · View only",
    noValue: "Not configured", protected: "Protected system charge · View only",
  },
  es: {
    readOnly: "Demo · Solo consulta. Los cambios están deshabilitados.",
    update: "Este JSON de la demo no incluye el catálogo completo de cargos y su matriz de upgrades. Actualiza los datos de la demo y pulsa Refrescar para reintentar.",
    refresh: "Refrescar", detail: "Detalle del cargo", select: "Selecciona el nombre de un cargo para consultar su detalle.",
    currency: "Moneda guardada · Sin conversión", order: "Orden de categorías guardado · Solo consulta",
    noValue: "Sin configurar", protected: "Cargo de sistema protegido · Solo consulta",
  },
  ca: {
    readOnly: "Demo · Només consulta. Els canvis estan deshabilitats.",
    update: "Aquest JSON de la demo no inclou el catàleg complet de càrrecs i la matriu d'upgrades. Actualitza les dades de la demo i prem Refrescar per tornar-ho a provar.",
    refresh: "Refrescar", detail: "Detall del càrrec", select: "Selecciona el nom d'un càrrec per consultar-ne el detall.",
    currency: "Moneda desada · Sense conversió", order: "Ordre de categories desat · Només consulta",
    noValue: "Sense configurar", protected: "Càrrec de sistema protegit · Només consulta",
  },
};

const CATEGORY_OPTIONS = [
  { value: "manual", label: "Manual" },
  { value: "penalty", label: "Penalty" },
  { value: "consumption", label: "Consumption" },
  { value: "fee", label: "Fee" },
  { value: "tax", label: "Tax" },
  { value: "upgrade", label: "Upgrade" },
];

const UPGRADE_CHARGE_CODE = "room_upgrade";
const UPGRADE_MATRIX_COLUMN_WIDTH = 210;
const upgradeRuleKey = (fromCategoryId, toCategoryId) =>
  `${Number(fromCategoryId)}:${Number(toCategoryId)}`;
const currencySymbol = (currency) => {
  try {
    return (
      new Intl.NumberFormat("es-ES", {
        style: "currency",
        currency,
        currencyDisplay: "narrowSymbol",
      })
        .formatToParts(0)
        .find((part) => part.type === "currency")?.value || currency
    );
  } catch (_error) {
    return currency;
  }
};

const emptyDraft = {
  id: null,
  code: "",
  name: "",
  description: "",
  category: "manual",
  calculation_mode: "manual",
  default_amount: "0",
  currency: "EUR",
  is_active: true,
  sort_order: 100,
};

const money = (value, currency = "EUR", currencyCatalog = []) =>
  formatCurrencyAmount(value || 0, currency, { catalog: currencyCatalog });
const categoryLabel = (value) =>
  CATEGORY_OPTIONS.find((option) => option.value === value)?.label || "Manual";

const SETTINGS_CHARGES_BUSINESS_UNIT_ERROR =
  "Select an active business unit before managing reservation charge types.";

const normalizeSettingsChargesBusinessUnitId = (value) => {
  if (value == null || String(value).trim() === "") return null;
  const normalized = String(value).trim();
  if (!/^\d+$/.test(normalized)) return null;
  const numericValue = Number(normalized);
  return Number.isSafeInteger(numericValue) && numericValue > 0
    ? numericValue
    : null;
};

export const resolveSettingsChargesBusinessUnitId = ({
  queryBusinessUnitId,
  user,
} = {}) => {
  for (const candidate of [
    queryBusinessUnitId,
    user?.active_business_unit_id,
    user?.business_unit_id,
  ]) {
    const businessUnitId = normalizeSettingsChargesBusinessUnitId(candidate);
    if (businessUnitId) return businessUnitId;
  }
  return null;
};

export const getChargeAmountPrecisionError = (value, currency, currencyCatalog = []) => {
  if (hasValidCurrencyPrecision(value, currency, currencyCatalog)) return null;
  const code = String(currency || "").trim().toUpperCase();
  const minorUnits = getCurrencyMinorUnits(code, currencyCatalog);
  return `${code} amounts accept ${minorUnits === 0 ? "whole units only" : `up to ${minorUnits} decimal places`}.`;
};

export default function SettingsCharges() {
  const demoModeActive = isDemoMode();
  const [searchParams] = useSearchParams();
  const { themeId, languageId } = usePmsTheme();
  const demoCopy = DEMO_CHARGES_MESSAGES[languageId] || DEMO_CHARGES_MESSAGES.en;
  const { baseCurrency, currencyCatalog = [] } = useCurrencyDisplay();
  const isLightTheme = themeId === "light";
  const [sessionUser, setSessionUser] = useState(() => demoModeActive ? readDemoUser() : readStoredUser());
  const currentUser = (demoModeActive ? readDemoUser() : sessionUser) || {};
  const permissions = currentUser?.permissions || {};
  const canManage =
    !demoModeActive && (Number(currentUser?.id) === 1 ||
    Boolean(permissions.access_settings) ||
    Boolean(
      permissions["hotel_settings.manage_reservation_charges_actions"]
    ) ||
    Boolean(permissions["hotel_settings.manage_extras_actions"]));
  const businessUnitId = demoModeActive ? DEMO_BUSINESS_UNIT_ID : resolveSettingsChargesBusinessUnitId({
    queryBusinessUnitId: searchParams.get("business_unit_id"),
    user: sessionUser,
  });

  const [chargeTypes, setChargeTypes] = useState([]);
  const [draft, setDraft] = useState(() => ({ ...emptyDraft, currency: baseCurrency || "EUR" }));
  const [loading, setLoading] = useState(demoModeActive);
  const [demoUpgradeContext, setDemoUpgradeContext] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [query, setQuery] = useState("");
  const [upgradeEditor, setUpgradeEditor] = useState(null);
  const [upgradeRules, setUpgradeRules] = useState({});
  const [upgradeLoading, setUpgradeLoading] = useState(false);
  const [upgradeSaving, setUpgradeSaving] = useState(false);
  const [upgradeError, setUpgradeError] = useState("");
  const [draggedUpgradeCategoryId, setDraggedUpgradeCategoryId] = useState(null);

  useEffect(() => {
    if (demoModeActive) return undefined;
    const syncSessionUser = () => { if (!isDemoMode()) setSessionUser(readStoredUser()); };
    window.addEventListener("palantir-session-updated", syncSessionUser);
    return () => {
      window.removeEventListener("palantir-session-updated", syncSessionUser);
    };
  }, [demoModeActive]);

  const loadChargeTypes = async () => {
    if (!businessUnitId) {
      setChargeTypes([]);
      setLoading(false);
      setError(SETTINGS_CHARGES_BUSINESS_UNIT_ERROR);
      return;
    }
    setLoading(true);
    setError("");
    if (demoModeActive) {
      setDraft({ ...emptyDraft, id: null });
      setDemoUpgradeContext(null);
      setUpgradeEditor(null);
      setUpgradeRules({});
      try {
        const { data } = await axios.get("/api/demo/charges-settings", {
          params: { business_unit_id: DEMO_BUSINESS_UNIT_ID },
        });
        if (!data || !Array.isArray(data.chargeTypes) ||
          !Object.prototype.hasOwnProperty.call(data, "upgradeContext") ||
          data.chargeTypes.some((type) => !type || type.business_unit_id !== DEMO_BUSINESS_UNIT_ID)) {
          throw new Error("Incomplete demo charge catalog");
        }
        const upgradeType = data.chargeTypes.find((type) => type.code === UPGRADE_CHARGE_CODE);
        const context = data.upgradeContext;
        if (upgradeType && (!context || context.charge_type?.id !== upgradeType.id ||
          context.charge_type?.business_unit_id !== DEMO_BUSINESS_UNIT_ID ||
          !Array.isArray(context.categories) || !Array.isArray(context.rules) ||
          !Array.isArray(context.category_order) || typeof context.currency !== "string" || !context.currency)) {
          throw new Error("Incomplete demo upgrade matrix");
        }
        if (!upgradeType && context !== null) throw new Error("Unexpected demo upgrade matrix");
        if (context) {
          const categoryIds = new Set(context.categories.map((category) => category.id));
          if (context.rules.some((rule) => rule.business_unit_id !== DEMO_BUSINESS_UNIT_ID ||
            !categoryIds.has(rule.from_room_category_id) || !categoryIds.has(rule.to_room_category_id))) {
            throw new Error("Invalid demo upgrade references");
          }
        }
        setChargeTypes(data.chargeTypes);
        setDemoUpgradeContext(context);
      } catch (_error) {
        setChargeTypes([]);
        setError(demoCopy.update);
      } finally {
        setLoading(false);
      }
      return;
    }
    try {
      const { data } = await axios.get("/api/reservation-charges/types", {
        params: { business_unit_id: businessUnitId },
      });
      setChargeTypes(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err?.response?.data?.error || "Could not load reservation charge types.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!businessUnitId) {
      setChargeTypes([]);
      setLoading(false);
      setError(SETTINGS_CHARGES_BUSINESS_UNIT_ERROR);
      return;
    }
    void loadChargeTypes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessUnitId, demoModeActive]);

  useEffect(() => {
    if (demoModeActive) return;
    setDraft((current) =>
      current.id
        ? current
        : { ...current, currency: baseCurrency || "EUR" }
    );
  }, [baseCurrency, demoModeActive]);

  const filteredTypes = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return chargeTypes;
    return chargeTypes.filter((type) =>
      [type.name, type.code, type.description, type.category]
        .some((value) => String(value || "").toLowerCase().includes(needle))
    );
  }, [chargeTypes, query]);

  const configuredUpgradeRuleCount = useMemo(
    () =>
      Object.values(upgradeRules).filter(
        (rule) => String(rule?.value ?? "").trim() !== ""
      ).length,
    [upgradeRules]
  );

  const stats = useMemo(() => {
    const protectedCount = chargeTypes.filter((type) => type.is_protected).length;
    const manualCount = chargeTypes.filter((type) => !type.is_system).length;
    const activeCount = chargeTypes.filter((type) => type.is_active).length;
    return { protectedCount, manualCount, activeCount };
  }, [chargeTypes]);

  const startNew = () => {
    if (demoModeActive || isDemoMode()) return;
    setDraft({ ...emptyDraft, currency: baseCurrency || "EUR" });
    setError("");
    setNotice("");
  };

  const openUpgradeEditor = async (type) => {
    if (demoModeActive || isDemoMode()) {
      if (!demoModeActive || loading || error || !demoUpgradeContext || demoUpgradeContext.charge_type?.id !== type.id) return;
      const nextRules = {};
      for (const rule of demoUpgradeContext.rules) {
        nextRules[upgradeRuleKey(rule.from_room_category_id, rule.to_room_category_id)] = {
          calculation_mode: rule.calculation_mode,
          value: rule.value == null ? "" : String(rule.value),
          original_calculation_mode: rule.calculation_mode,
          original_value: rule.value == null ? "" : String(rule.value),
        };
      }
      setUpgradeRules(nextRules);
      setUpgradeEditor({
        type: demoUpgradeContext.charge_type,
        categories: demoUpgradeContext.categories,
        currency: demoUpgradeContext.currency,
      });
      setUpgradeError("");
      setUpgradeLoading(false);
      setDraggedUpgradeCategoryId(null);
      return;
    }
    setUpgradeEditor({
      type,
      categories: [],
      currency: type.currency || baseCurrency || "EUR",
    });
    setUpgradeRules({});
    setUpgradeError("");
    setDraggedUpgradeCategoryId(null);
    setUpgradeLoading(true);

    try {
      const { data } = await axios.get(
        `/api/reservation-charges/types/${type.id}/upgrade-rules`
      );
      const nextRules = {};
      for (const rule of Array.isArray(data?.rules) ? data.rules : []) {
        nextRules[
          upgradeRuleKey(
            rule.from_room_category_id,
            rule.to_room_category_id
          )
        ] = {
          calculation_mode: rule.calculation_mode,
          value: String(Number(rule.value)),
          original_calculation_mode: rule.calculation_mode,
          original_value: String(Number(rule.value)),
        };
      }
      setUpgradeRules(nextRules);
      setUpgradeEditor({
        type: data?.charge_type || type,
        categories: Array.isArray(data?.categories) ? data.categories : [],
        currency: data?.currency || type.currency || baseCurrency || "EUR",
      });
    } catch (err) {
      setUpgradeError(
        err?.response?.data?.error || "Could not load the upgrade matrix."
      );
    } finally {
      setUpgradeLoading(false);
    }
  };

  const closeUpgradeEditor = (force = false) => {
    if (upgradeSaving && !force) return;
    setUpgradeEditor(null);
    setUpgradeRules({});
    setUpgradeError("");
    setDraggedUpgradeCategoryId(null);
  };

  useEffect(() => {
    if (!demoModeActive || !upgradeEditor) return undefined;
    const closeOnEscape = (event) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setUpgradeEditor(null);
      setUpgradeRules({});
      setUpgradeError("");
      setDraggedUpgradeCategoryId(null);
    };
    document.addEventListener("keydown", closeOnEscape);
    return () => document.removeEventListener("keydown", closeOnEscape);
  }, [demoModeActive, upgradeEditor]);

  const reorderUpgradeCategories = (sourceCategoryId, targetCategoryId) => {
    if (demoModeActive || isDemoMode()) return;
    const sourceId = Number(sourceCategoryId);
    const targetId = Number(targetCategoryId);
    if (!sourceId || !targetId || sourceId === targetId) return;

    setUpgradeEditor((current) => {
      if (!current) return current;
      const sourceIndex = current.categories.findIndex(
        (category) => Number(category.id) === sourceId
      );
      const targetIndex = current.categories.findIndex(
        (category) => Number(category.id) === targetId
      );
      if (sourceIndex < 0 || targetIndex < 0) return current;

      const categories = [...current.categories];
      const [movedCategory] = categories.splice(sourceIndex, 1);
      categories.splice(targetIndex, 0, movedCategory);
      return { ...current, categories };
    });
    setUpgradeError("");
  };

  const moveUpgradeCategory = (categoryId, offset) => {
    if (demoModeActive || isDemoMode()) return;
    const normalizedId = Number(categoryId);
    setUpgradeEditor((current) => {
      if (!current) return current;
      const currentIndex = current.categories.findIndex(
        (category) => Number(category.id) === normalizedId
      );
      const targetIndex = currentIndex + offset;
      if (
        currentIndex < 0 ||
        targetIndex < 0 ||
        targetIndex >= current.categories.length
      ) {
        return current;
      }

      const categories = [...current.categories];
      [categories[currentIndex], categories[targetIndex]] = [
        categories[targetIndex],
        categories[currentIndex],
      ];
      return { ...current, categories };
    });
    setUpgradeError("");
  };

  const updateUpgradeRuleMode = (
    fromRoomCategoryId,
    toRoomCategoryId,
    calculationMode
  ) => {
    if (demoModeActive || isDemoMode()) return;
    const key = upgradeRuleKey(fromRoomCategoryId, toRoomCategoryId);
    setUpgradeRules((current) => ({
      ...current,
      [key]: {
        ...current[key],
        calculation_mode: calculationMode,
        value: current[key]?.value || "",
      },
    }));
    setUpgradeError("");
  };

  const updateUpgradeRuleValue = (
    fromRoomCategoryId,
    toRoomCategoryId,
    value
  ) => {
    if (demoModeActive || isDemoMode()) return;
    const key = upgradeRuleKey(fromRoomCategoryId, toRoomCategoryId);
    setUpgradeRules((current) => ({
      ...current,
      [key]: {
        ...current[key],
        calculation_mode: current[key]?.calculation_mode || "percentage",
        value,
      },
    }));
    setUpgradeError("");
  };

  const saveUpgradeRules = async () => {
    if (demoModeActive || isDemoMode()) return;
    if (!upgradeEditor?.type?.id) return;

    const rules = Object.entries(upgradeRules)
      .filter(([, rule]) => String(rule?.value ?? "").trim() !== "")
      .map(([key, rule]) => {
        const [fromRoomCategoryId, toRoomCategoryId] = key
          .split(":")
          .map(Number);
        return {
          from_room_category_id: fromRoomCategoryId,
          to_room_category_id: toRoomCategoryId,
          calculation_mode: rule.calculation_mode || "percentage",
          value: Number(rule.value),
        };
      });
    if (rules.some((rule) => !Number.isFinite(rule.value) || rule.value <= 0)) {
      setUpgradeError(
        "Every configured upgrade must have a value greater than zero."
      );
      return;
    }
    const changedFixedRule = Object.values(upgradeRules).find((rule) => {
      if (String(rule?.value ?? "").trim() === "") return false;
      const mode = rule.calculation_mode || "percentage";
      if (mode !== "fixed_amount") return false;
      const unchanged =
        rule.original_calculation_mode === mode &&
        Number(rule.original_value) === Number(rule.value);
      return !unchanged && !hasValidCurrencyPrecision(
        rule.value,
        upgradeEditor.currency,
        currencyCatalog
      );
    });
    if (changedFixedRule) {
      setUpgradeError(getChargeAmountPrecisionError(
        changedFixedRule.value,
        upgradeEditor.currency,
        currencyCatalog
      ));
      return;
    }

    setUpgradeSaving(true);
    setUpgradeError("");
    try {
      await axios.put(
        `/api/reservation-charges/types/${upgradeEditor.type.id}/upgrade-rules`,
        {
          rules,
          category_order: upgradeEditor.categories.map((category) =>
            Number(category.id)
          ),
        }
      );
      setNotice("Upgrade matrix updated.");
      closeUpgradeEditor(true);
    } catch (err) {
      setUpgradeError(
        err?.response?.data?.error || "Could not save the upgrade matrix."
      );
    } finally {
      setUpgradeSaving(false);
    }
  };

  const editType = (type, { viewOnly = false } = {}) => {
    if (demoModeActive || isDemoMode()) {
      if (!demoModeActive || !viewOnly || loading || error) return;
      setDraft({
        ...type,
        name: type.name ?? "", code: type.code ?? "", description: type.description ?? "",
        category: type.category ?? "", calculation_mode: type.calculation_mode ?? "",
        default_amount: type.default_amount == null ? "" : String(type.default_amount),
        currency: type.currency ?? "", sort_order: type.sort_order ?? "",
      });
      if (type.code === UPGRADE_CHARGE_CODE) void openUpgradeEditor(type);
      return;
    }
    if (type.code === UPGRADE_CHARGE_CODE) {
      void openUpgradeEditor(type);
      return;
    }
    setDraft({
      id: type.id,
      code: type.code || "",
      name: type.name || "",
      description: type.description || "",
      category: type.category || "manual",
      calculation_mode: type.calculation_mode || "manual",
      default_amount: type.default_amount == null ? "" : String(type.default_amount),
      currency: type.currency || "EUR",
      original_default_amount: type.default_amount == null ? "" : String(type.default_amount),
      original_currency: type.currency || "EUR",
      is_active: Boolean(type.is_active),
      sort_order: Number(type.sort_order || 100),
      is_system: Boolean(type.is_system),
      is_protected: Boolean(type.is_protected),
    });
    setError("");
    setNotice("");
  };

  const updateDraft = (key, value) => {
    if (demoModeActive || isDemoMode()) return;
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const saveDraft = async () => {
    if (demoModeActive || isDemoMode()) return;
    if (!businessUnitId) {
      setError(SETTINGS_CHARGES_BUSINESS_UNIT_ERROR);
      return;
    }
    if (!draft.name.trim()) {
      setError("Name is required.");
      return;
    }
    const normalizedCurrency = String(draft.currency || baseCurrency || "").trim().toUpperCase();
    const monetaryFieldsUnchanged = Boolean(draft.id) &&
      normalizedCurrency === String(draft.original_currency || "").trim().toUpperCase() &&
      Number(draft.default_amount || 0) === Number(draft.original_default_amount || 0);
    if (!monetaryFieldsUnchanged) {
      const precisionError = getChargeAmountPrecisionError(
        draft.default_amount || 0,
        normalizedCurrency,
        currencyCatalog
      );
      if (precisionError) {
        setError(precisionError);
        return;
      }
    }

    setSaving(true);
    setError("");
    setNotice("");
    try {
      const payload = {
        business_unit_id: Number(businessUnitId),
        code: draft.code,
        name: draft.name,
        description: draft.description,
        category: draft.category,
        calculation_mode: "manual",
        default_amount: Number(draft.default_amount || 0),
        currency: normalizedCurrency.slice(0, 3),
        is_active: Boolean(draft.is_active),
        sort_order: Number(draft.sort_order || 100),
      };

      if (draft.id) {
        await axios.put(`/api/reservation-charges/types/${draft.id}`, payload);
        setNotice("Charge type updated.");
      } else {
        await axios.post("/api/reservation-charges/types", payload);
        setNotice("Charge type created.");
      }

      await loadChargeTypes();
      setDraft({ ...emptyDraft, currency: baseCurrency || "EUR" });
    } catch (err) {
      setError(err?.response?.data?.error || "Could not save charge type.");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (type) => {
    if (demoModeActive || isDemoMode()) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      await axios.patch(`/api/reservation-charges/types/${type.id}/toggle-active`);
      await loadChargeTypes();
      setNotice(type.is_active ? "Charge type deactivated." : "Charge type activated.");
    } catch (err) {
      setError(err?.response?.data?.error || "Could not update charge type status.");
    } finally {
      setSaving(false);
    }
  };

  const deleteType = async (type) => {
    if (demoModeActive || isDemoMode()) return;
    if (type.is_protected) return;

    setSaving(true);
    setError("");
    setNotice("");
    try {
      await axios.delete(`/api/reservation-charges/types/${type.id}`);
      await loadChargeTypes();
      if (Number(draft.id) === Number(type.id)) setDraft({ ...emptyDraft });
      setNotice("Charge type deleted.");
    } catch (err) {
      setError(err?.response?.data?.error || "Could not delete charge type.");
      if (err?.response?.data?.charge_type) {
        await loadChargeTypes();
      }
    } finally {
      setSaving(false);
    }
  };

  const shellClass = `chargesSettings${isLightTheme ? " chargesSettings--light" : ""}${demoModeActive ? " chargesSettings--demo" : ""}`;

  return (
    <div className={shellClass}>
      <style>{`
        .chargesSettings {
          min-height: 0;
          height: 100%;
          display: grid;
          grid-template-rows: auto 1fr;
          gap: 14px;
          color: rgba(248, 250, 252, 0.96);
          padding: 18px;
          overflow: hidden;
        }
        .chargesSettings--light {
          color: #2f3744;
        }
        .chargesSettings--demo.chargesSettings--light h2.chargesSettings__title,
        .chargesSettings--demo.chargesSettings--light h3.chargesSettings__panelTitle,
        .chargesSettings--demo.chargesSettings--light .chargesSettings__upgradeHeader h3 {
          color: #25344d;
        }
        .chargesSettings--demo.chargesSettings--light .chargesSettings__alert--error {
          color: #8f1838;
          background: #fff1f2;
          border-color: #f3bcc8;
        }
        .chargesSettings__hero {
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          background: linear-gradient(135deg, rgba(14, 165, 233, 0.16), rgba(34, 197, 94, 0.10));
          padding: 16px;
          display: flex;
          justify-content: space-between;
          gap: 16px;
          flex-wrap: wrap;
        }
        .chargesSettings--light .chargesSettings__hero {
          border-color: rgba(24, 54, 69, 0.12);
          background: linear-gradient(135deg, rgba(246, 250, 252, 0.96), rgba(232, 244, 242, 0.9));
        }
        .chargesSettings__eyebrow {
          margin: 0 0 4px;
          color: #7dd3fc;
          font-size: 0.72rem;
          font-weight: 850;
          letter-spacing: 0.14em;
          text-transform: uppercase;
        }
        .chargesSettings--light .chargesSettings__eyebrow {
          color: #256377;
        }
        .chargesSettings__title {
          margin: 0;
          font-size: 1.35rem;
          line-height: 1.12;
          font-weight: 850;
          letter-spacing: 0;
        }
        .chargesSettings__copy {
          margin: 7px 0 0;
          max-width: 720px;
          color: rgba(203, 213, 225, 0.78);
          font-size: 0.9rem;
          line-height: 1.45;
          font-weight: 650;
        }
        .chargesSettings--light .chargesSettings__copy {
          color: rgba(47, 55, 68, 0.72);
        }
        .chargesSettings__stats {
          display: flex;
          gap: 8px;
          flex-wrap: wrap;
          align-items: flex-start;
        }
        .chargesSettings__stat {
          min-width: 116px;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          background: rgba(255, 255, 255, 0.055);
          padding: 9px 11px;
        }
        .chargesSettings--light .chargesSettings__stat {
          border-color: rgba(24, 54, 69, 0.1);
          background: rgba(255, 255, 255, 0.72);
        }
        .chargesSettings__stat span {
          display: block;
          color: rgba(203, 213, 225, 0.7);
          font-size: 0.72rem;
          font-weight: 800;
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .chargesSettings--light .chargesSettings__stat span {
          color: rgba(47, 55, 68, 0.58);
        }
        .chargesSettings__stat strong {
          display: block;
          margin-top: 3px;
          font-size: 1.15rem;
          font-weight: 850;
        }
        .chargesSettings__body {
          min-height: 0;
          display: grid;
          grid-template-columns: minmax(0, 1.45fr) minmax(340px, 0.8fr);
          gap: 14px;
          overflow: hidden;
        }
        .chargesSettings__panel {
          min-height: 0;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.12);
          background: rgba(15, 23, 42, 0.72);
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }
        .chargesSettings--light .chargesSettings__panel {
          border-color: rgba(24, 54, 69, 0.1);
          background: rgba(255, 255, 255, 0.84);
        }
        .chargesSettings__panelHeader {
          padding: 13px 14px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
          display: flex;
          justify-content: space-between;
          gap: 10px;
          align-items: center;
        }
        .chargesSettings--light .chargesSettings__panelHeader {
          border-bottom-color: rgba(24, 54, 69, 0.08);
        }
        .chargesSettings__panelTitle {
          margin: 0;
          font-size: 0.98rem;
          font-weight: 850;
        }
        .chargesSettings__search {
          min-height: 38px;
          width: min(280px, 42vw);
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.14);
          background: rgba(2, 6, 23, 0.48);
          color: inherit;
          padding: 0 11px;
          font-weight: 700;
        }
        .chargesSettings--light .chargesSettings__search {
          border-color: rgba(24, 54, 69, 0.12);
          background: rgba(255, 255, 255, 0.86);
        }
        .chargesSettings__list {
          min-height: 0;
          overflow: auto;
          padding: 10px;
          display: grid;
          gap: 8px;
        }
        .chargesSettings__row {
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.1);
          background: rgba(255, 255, 255, 0.045);
          padding: 11px;
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          gap: 10px;
          align-items: center;
        }
        .chargesSettings--light .chargesSettings__row {
          border-color: rgba(24, 54, 69, 0.08);
          background: rgba(248, 250, 252, 0.68);
        }
        .chargesSettings__rowTitle {
          display: flex;
          gap: 8px;
          align-items: center;
          flex-wrap: wrap;
          font-weight: 850;
        }
        .chargesSettings__rowMeta {
          margin-top: 5px;
          display: flex;
          gap: 7px;
          flex-wrap: wrap;
          color: rgba(203, 213, 225, 0.72);
          font-size: 0.78rem;
          font-weight: 700;
        }
        .chargesSettings--light .chargesSettings__rowMeta {
          color: rgba(47, 55, 68, 0.68);
        }
        .chargesSettings__badge {
          border-radius: 999px;
          border: 1px solid rgba(125, 211, 252, 0.22);
          background: rgba(14, 165, 233, 0.1);
          color: #bae6fd;
          padding: 3px 8px;
          font-size: 0.68rem;
          font-weight: 850;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .chargesSettings--light .chargesSettings__badge {
          color: #256377;
          background: rgba(37, 99, 119, 0.08);
          border-color: rgba(37, 99, 119, 0.16);
        }
        .chargesSettings__badge--muted {
          color: rgba(203, 213, 225, 0.72);
          background: rgba(148, 163, 184, 0.08);
          border-color: rgba(148, 163, 184, 0.14);
        }
        .chargesSettings--light .chargesSettings__badge--muted {
          color: rgba(47, 55, 68, 0.62);
        }
        .chargesSettings__rowActions {
          display: flex;
          gap: 7px;
          flex-wrap: wrap;
          justify-content: flex-end;
        }
        .chargesSettings__button {
          min-height: 36px;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.14);
          background: rgba(255, 255, 255, 0.06);
          color: inherit;
          padding: 0 11px;
          font-weight: 850;
        }
        .chargesSettings--light .chargesSettings__button {
          border-color: rgba(24, 54, 69, 0.12);
          background: rgba(255, 255, 255, 0.78);
        }
        .chargesSettings__button--primary {
          border-color: rgba(74, 222, 128, 0.35);
          background: rgba(34, 197, 94, 0.16);
        }
        .chargesSettings__button--danger {
          border-color: rgba(248, 113, 113, 0.28);
          background: rgba(127, 29, 29, 0.18);
        }
        .chargesSettings__button:disabled {
          opacity: 0.58;
          cursor: not-allowed;
        }
        .chargesSettings__form {
          padding: 14px;
          display: grid;
          gap: 12px;
          overflow: auto;
        }
        .chargesSettings__field {
          display: grid;
          gap: 6px;
        }
        .chargesSettings__label {
          font-size: 0.74rem;
          font-weight: 850;
          color: rgba(226, 232, 240, 0.78);
          text-transform: uppercase;
          letter-spacing: 0.08em;
        }
        .chargesSettings--light .chargesSettings__label {
          color: rgba(47, 55, 68, 0.68);
        }
        .chargesSettings__input,
        .chargesSettings__select,
        .chargesSettings__textarea {
          width: 100%;
          border-radius: 8px;
          border: 1px solid rgba(255, 255, 255, 0.14);
          background: rgba(2, 6, 23, 0.48);
          color: inherit;
          padding: 9px 11px;
          font-weight: 700;
        }
        .chargesSettings__textarea {
          min-height: 88px;
          resize: vertical;
        }
        .chargesSettings--light .chargesSettings__input,
        .chargesSettings--light .chargesSettings__select,
        .chargesSettings--light .chargesSettings__textarea {
          border-color: rgba(24, 54, 69, 0.12);
          background: rgba(255, 255, 255, 0.86);
        }
        .chargesSettings__fieldGrid {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        .chargesSettings__switch {
          display: flex;
          gap: 9px;
          align-items: center;
          font-weight: 750;
        }
        .chargesSettings__alert {
          border-radius: 8px;
          padding: 10px 12px;
          font-size: 0.84rem;
          font-weight: 750;
        }
        .chargesSettings__alert--error {
          color: #fecaca;
          border: 1px solid rgba(248, 113, 113, 0.3);
          background: rgba(127, 29, 29, 0.22);
        }
        .chargesSettings__alert--notice {
          color: #bbf7d0;
          border: 1px solid rgba(74, 222, 128, 0.26);
          background: rgba(22, 163, 74, 0.12);
        }
        .chargesSettings__upgradeOverlay {
          position: fixed;
          inset: 0;
          z-index: 1300;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 12px;
          background: rgba(2, 6, 23, 0.78);
          backdrop-filter: blur(8px);
        }
        .chargesSettings__upgradeModal {
          width: calc(100vw - 24px);
          max-width: none;
          max-height: calc(100vh - 24px);
          border-radius: 10px;
          border: 1px solid rgba(125, 211, 252, 0.22);
          background: linear-gradient(145deg, #0b1220, #111827);
          box-shadow: 0 34px 90px rgba(0, 0, 0, 0.55);
          display: grid;
          grid-template-rows: auto auto minmax(0, 1fr) auto;
          overflow: hidden;
        }
        .chargesSettings--light .chargesSettings__upgradeModal {
          border-color: rgba(24, 54, 69, 0.16);
          background: linear-gradient(145deg, #ffffff, #f4f8fa);
          box-shadow: 0 28px 70px rgba(24, 54, 69, 0.2);
        }
        .chargesSettings__upgradeHeader {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 16px;
          padding: 18px 20px 14px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.1);
        }
        .chargesSettings--light .chargesSettings__upgradeHeader {
          border-bottom-color: rgba(24, 54, 69, 0.1);
        }
        .chargesSettings__upgradeHeader h3 {
          margin: 2px 0 0;
          font-size: 1.18rem;
          font-weight: 900;
        }
        .chargesSettings__upgradeIntro {
          padding: 12px 20px;
          color: rgba(203, 213, 225, 0.76);
          font-size: 0.82rem;
          line-height: 1.45;
          font-weight: 650;
        }
        .chargesSettings--light .chargesSettings__upgradeIntro {
          color: rgba(47, 55, 68, 0.72);
        }
        .chargesSettings__upgradeOrder {
          margin-top: 12px;
          padding: 10px;
          border: 1px solid rgba(125, 211, 252, 0.16);
          border-radius: 9px;
          background: rgba(2, 6, 23, 0.28);
        }
        .chargesSettings--light .chargesSettings__upgradeOrder {
          border-color: rgba(24, 54, 69, 0.12);
          background: rgba(231, 240, 244, 0.52);
        }
        .chargesSettings__upgradeOrderHeader {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 12px;
          margin-bottom: 8px;
        }
        .chargesSettings__upgradeOrderTitle {
          color: #bae6fd;
          font-size: 0.76rem;
          font-weight: 900;
        }
        .chargesSettings--light .chargesSettings__upgradeOrderTitle {
          color: #256377;
        }
        .chargesSettings__upgradeOrderHint {
          color: rgba(203, 213, 225, 0.56);
          font-size: 0.7rem;
          font-weight: 700;
        }
        .chargesSettings--light .chargesSettings__upgradeOrderHint {
          color: rgba(47, 55, 68, 0.56);
        }
        .chargesSettings__upgradeOrderList {
          display: flex;
          gap: 7px;
          min-width: 0;
          overflow-x: auto;
          padding-bottom: 3px;
        }
        .chargesSettings__upgradeOrderItem {
          display: grid;
          grid-template-columns: auto auto minmax(90px, 1fr) auto;
          align-items: center;
          gap: 6px;
          flex: 0 0 245px;
          min-width: 0;
          padding: 5px 6px;
          border: 1px solid rgba(125, 211, 252, 0.2);
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.82);
          color: #e2e8f0;
          transition: opacity 120ms ease, border-color 120ms ease,
            transform 120ms ease;
        }
        .chargesSettings__upgradeOrderItem[draggable="true"] {
          cursor: grab;
        }
        .chargesSettings__upgradeOrderItem--dragging {
          opacity: 0.46;
          border-color: #38bdf8;
          transform: scale(0.98);
        }
        .chargesSettings--light .chargesSettings__upgradeOrderItem {
          border-color: rgba(37, 99, 119, 0.18);
          background: #ffffff;
          color: #173d49;
        }
        .chargesSettings__upgradeOrderHandle {
          color: rgba(125, 211, 252, 0.66);
          font-size: 0.76rem;
          font-weight: 900;
          letter-spacing: -0.18em;
          user-select: none;
        }
        .chargesSettings__upgradeOrderPosition {
          display: inline-grid;
          place-items: center;
          width: 22px;
          height: 22px;
          border-radius: 50%;
          background: rgba(14, 116, 144, 0.4);
          color: #e0f2fe;
          font-size: 0.68rem;
          font-weight: 900;
        }
        .chargesSettings--light .chargesSettings__upgradeOrderPosition {
          background: #d6edf3;
          color: #173d49;
        }
        .chargesSettings__upgradeOrderName {
          min-width: 0;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          font-size: 0.72rem;
          font-weight: 850;
        }
        .chargesSettings__upgradeOrderActions {
          display: flex;
          gap: 3px;
        }
        .chargesSettings__upgradeOrderButton {
          display: inline-grid;
          place-items: center;
          width: 23px;
          height: 23px;
          border: 1px solid rgba(125, 211, 252, 0.18);
          border-radius: 50%;
          background: rgba(2, 6, 23, 0.34);
          color: #bae6fd;
          padding: 0;
          font: inherit;
          font-size: 0.72rem;
          font-weight: 900;
          cursor: pointer;
        }
        .chargesSettings__upgradeOrderButton:disabled {
          opacity: 0.24;
          cursor: not-allowed;
        }
        .chargesSettings--light .chargesSettings__upgradeOrderButton {
          border-color: rgba(37, 99, 119, 0.16);
          background: #eef5f7;
          color: #256377;
        }
        .chargesSettings__upgradeMatrixWrap {
          min-height: 0;
          overflow: auto;
          margin: 0 20px 18px;
          isolation: isolate;
          overscroll-behavior: contain;
        }
        .chargesSettings__upgradeMatrix {
          min-width: 100%;
          border-collapse: separate;
          border-spacing: 0;
          table-layout: fixed;
        }
        .chargesSettings__upgradeMatrix th,
        .chargesSettings__upgradeMatrix td {
          width: ${UPGRADE_MATRIX_COLUMN_WIDTH}px;
          min-width: ${UPGRADE_MATRIX_COLUMN_WIDTH}px;
          max-width: ${UPGRADE_MATRIX_COLUMN_WIDTH}px;
          box-sizing: border-box;
          overflow: hidden;
          border-right: 1px solid rgba(255, 255, 255, 0.09);
          border-bottom: 1px solid rgba(255, 255, 255, 0.09);
          background: rgba(15, 23, 42, 0.88);
          padding: 8px;
          vertical-align: middle;
        }
        .chargesSettings--light .chargesSettings__upgradeMatrix th,
        .chargesSettings--light .chargesSettings__upgradeMatrix td {
          border-color: rgba(24, 54, 69, 0.1);
          background: rgba(255, 255, 255, 0.94);
        }
        .chargesSettings__upgradeMatrix thead th {
          position: sticky;
          top: 0;
          z-index: 3;
          height: 66px;
          color: #bae6fd;
          font-size: 0.76rem;
          font-weight: 900;
          text-align: center;
          line-height: 1.25;
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
          background: #291a35;
        }
        .chargesSettings--light .chargesSettings__upgradeMatrix thead th {
          color: #256377;
          background: #e7f0f4;
        }
        .chargesSettings__upgradeMatrix tbody th {
          position: sticky;
          left: 0;
          z-index: 2;
          color: #bae6fd;
          font-size: 0.76rem;
          font-weight: 900;
          text-align: left;
          line-height: 1.25;
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: break-word;
          background: #0f172a;
          box-shadow: 9px 0 12px -12px rgba(0, 0, 0, 0.9);
        }
        .chargesSettings--light .chargesSettings__upgradeMatrix tbody th {
          color: #256377;
          background: #ffffff;
          box-shadow: 9px 0 12px -12px rgba(24, 54, 69, 0.42);
        }
        .chargesSettings__upgradeCorner {
          left: 0;
          z-index: 5 !important;
          text-align: left !important;
          background: #291a35 !important;
        }
        .chargesSettings--light .chargesSettings__upgradeCorner {
          background: #e7f0f4 !important;
        }
        .chargesSettings__upgradeCorner span {
          display: block;
          color: rgba(203, 213, 225, 0.58);
          font-size: 0.66rem;
          letter-spacing: 0.08em;
        }
        .chargesSettings--light .chargesSettings__upgradeCorner span {
          color: rgba(47, 55, 68, 0.56);
        }
        .chargesSettings__upgradeSame {
          color: rgba(148, 163, 184, 0.56);
          text-align: center;
          font-size: 0.72rem;
          font-weight: 800;
          background: rgba(2, 6, 23, 0.38) !important;
        }
        .chargesSettings--light .chargesSettings__upgradeSame {
          background: rgba(226, 232, 240, 0.54) !important;
        }
        .chargesSettings__upgradeCell {
          display: grid;
          gap: 7px;
          min-width: 0;
          max-width: 100%;
          overflow: hidden;
        }
        .chargesSettings__upgradeCell input {
          width: 100%;
          min-width: 0;
          max-width: 100%;
          box-sizing: border-box;
          min-height: 34px;
          border-radius: 7px;
          border: 1px solid rgba(255, 255, 255, 0.14);
          background: rgba(2, 6, 23, 0.62);
          color: inherit;
          padding: 5px 8px;
          font-size: 0.76rem;
          font-weight: 800;
        }
        .chargesSettings--light .chargesSettings__upgradeCell input {
          border-color: rgba(24, 54, 69, 0.13);
          background: #ffffff;
        }
        .chargesSettings__upgradeValue {
          display: grid;
          grid-template-columns: minmax(0, 1fr) auto;
          align-items: stretch;
          gap: 6px;
        }
        .chargesSettings__upgradeValue input {
          appearance: textfield;
          -moz-appearance: textfield;
        }
        .chargesSettings__upgradeValue input::-webkit-inner-spin-button,
        .chargesSettings__upgradeValue input::-webkit-outer-spin-button {
          margin: 0;
          -webkit-appearance: none;
        }
        .chargesSettings__upgradeModeChips {
          display: grid;
          grid-template-rows: repeat(2, 1fr);
          gap: 4px;
        }
        .chargesSettings__upgradeModeChip {
          min-width: 34px;
          border: 1px solid rgba(125, 211, 252, 0.22);
          border-radius: 999px;
          background: rgba(15, 23, 42, 0.76);
          color: rgba(203, 213, 225, 0.68);
          padding: 1px 7px;
          font: inherit;
          font-size: 0.7rem;
          font-weight: 900;
          line-height: 1;
          cursor: pointer;
          transition: border-color 140ms ease, background 140ms ease,
            color 140ms ease;
        }
        .chargesSettings__upgradeModeChip:hover {
          border-color: rgba(125, 211, 252, 0.55);
          color: #bae6fd;
        }
        .chargesSettings__upgradeModeChip--selected {
          border-color: rgba(56, 189, 248, 0.78);
          background: rgba(14, 116, 144, 0.38);
          color: #e0f2fe;
        }
        .chargesSettings--light .chargesSettings__upgradeModeChip {
          border-color: rgba(37, 99, 119, 0.2);
          background: #eef5f7;
          color: rgba(37, 99, 119, 0.68);
        }
        .chargesSettings--light .chargesSettings__upgradeModeChip:hover {
          border-color: rgba(37, 99, 119, 0.5);
          color: #256377;
        }
        .chargesSettings--light .chargesSettings__upgradeModeChip--selected {
          border-color: #256377;
          background: #d6edf3;
          color: #173d49;
        }
        .chargesSettings__upgradeFooter {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 12px;
          padding: 13px 20px;
          border-top: 1px solid rgba(255, 255, 255, 0.1);
        }
        .chargesSettings--light .chargesSettings__upgradeFooter {
          border-top-color: rgba(24, 54, 69, 0.1);
        }
        .chargesSettings__upgradeLegend {
          color: rgba(203, 213, 225, 0.68);
          font-size: 0.76rem;
          font-weight: 700;
        }
        .chargesSettings--light .chargesSettings__upgradeLegend {
          color: rgba(47, 55, 68, 0.66);
        }
        .chargesSettings__upgradeFooterActions {
          display: flex;
          gap: 8px;
          flex: 0 0 auto;
        }
        @media (max-width: 980px) {
          .chargesSettings {
            overflow: auto;
          }
          .chargesSettings__body {
            grid-template-columns: 1fr;
            overflow: visible;
          }
          .chargesSettings__panel {
            min-height: 420px;
          }
          .chargesSettings__upgradeOverlay {
            padding: 10px;
          }
          .chargesSettings__upgradeModal {
            width: calc(100vw - 20px);
            max-height: calc(100vh - 20px);
          }
          .chargesSettings__upgradeFooter {
            align-items: flex-start;
            flex-direction: column;
          }
        }
      `}</style>

      <section className="chargesSettings__hero">
        <div>
          <p className="chargesSettings__eyebrow">Folio setup</p>
          <h2 className="chargesSettings__title">Reservation Charges</h2>
          <p className="chargesSettings__copy">
            {demoModeActive ? demoCopy.readOnly : <>Configure charge types used for penalties, minibar, late check-out and manual
            in-house or post-stay charges. Protected system types stay available for core workflows.</>}
          </p>
        </div>
        {!(demoModeActive && (loading || error)) && <div className="chargesSettings__stats">
          <div className="chargesSettings__stat">
            <span>Configured</span>
            <strong>{chargeTypes.length}</strong>
          </div>
          <div className="chargesSettings__stat">
            <span>Active</span>
            <strong>{stats.activeCount}</strong>
          </div>
          <div className="chargesSettings__stat">
            <span>Protected</span>
            <strong>{stats.protectedCount}</strong>
          </div>
          <div className="chargesSettings__stat">
            <span>Manual</span>
            <strong>{stats.manualCount}</strong>
          </div>
        </div>}
      </section>

      <section className="chargesSettings__body">
        <div className="chargesSettings__panel">
          <div className="chargesSettings__panelHeader">
            <h3 className="chargesSettings__panelTitle">Charge type catalog</h3>
            {demoModeActive && <button type="button" className="chargesSettings__button" disabled={loading}
              onClick={() => { void loadChargeTypes(); }}>{demoCopy.refresh}</button>}
            <input
              className="chargesSettings__search"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search charges"
            />
          </div>

          <div className="chargesSettings__list">
            {loading ? (
              <div className="chargesSettings__alert">Loading reservation charges...</div>
            ) : demoModeActive && error ? (
              <div className="chargesSettings__alert chargesSettings__alert--error" role="alert">{error}</div>
            ) : filteredTypes.length ? (
              filteredTypes.map((type) => (
                <div className="chargesSettings__row" key={type.id}>
                  <div>
                    <div className="chargesSettings__rowTitle">
                      {demoModeActive ? <button type="button" className="chargesSettings__button"
                        title={demoCopy.detail} onClick={() => editType(type, { viewOnly: true })}>{type.name}</button> : <span>{type.name}</span>}
                      {type.is_protected ? <span className="chargesSettings__badge">Protected</span> : null}
                      {type.is_system ? <span className="chargesSettings__badge chargesSettings__badge--muted">System</span> : null}
                      {!type.is_active ? <span className="chargesSettings__badge chargesSettings__badge--muted">Inactive</span> : null}
                    </div>
                    <div className="chargesSettings__rowMeta">
                      <span>{demoModeActive ? CATEGORY_OPTIONS.find((option) => option.value === type.category)?.label || type.category || "—" : categoryLabel(type.category)}</span>
                      <span>
                        {type.code === UPGRADE_CHARGE_CODE
                          ? "Hotel-defined matrix · Per night"
                          : demoModeActive ? (type.default_amount == null ? "—" : `${String(type.default_amount)} ${type.currency ?? "—"}`) : money(type.default_amount, type.currency, currencyCatalog)}
                      </span>
                    </div>
                  </div>

                  <div className="chargesSettings__rowActions">
                    <button
                      type="button"
                      className="chargesSettings__button"
                      onClick={() => editType(type)}
                      disabled={saving || !canManage}
                    >
                      {type.code === UPGRADE_CHARGE_CODE ? "Configure" : "Edit"}
                    </button>
                    <button
                      type="button"
                      className="chargesSettings__button"
                      onClick={() => toggleActive(type)}
                      disabled={saving || !canManage || type.is_protected}
                    >
                      {type.is_active ? "Deactivate" : "Activate"}
                    </button>
                    <button
                      type="button"
                      className="chargesSettings__button chargesSettings__button--danger"
                      onClick={() => deleteType(type)}
                      disabled={saving || !canManage || type.is_protected}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="chargesSettings__alert">No charge types found.</div>
            )}
          </div>
        </div>

        <div className="chargesSettings__panel">
          <div className="chargesSettings__panelHeader">
            <h3 className="chargesSettings__panelTitle">
              {demoModeActive ? demoCopy.detail : draft.id ? "Edit charge type" : "New charge type"}
            </h3>
            <button type="button" className="chargesSettings__button" onClick={startNew} disabled={saving || !canManage}>
              New
            </button>
          </div>

          {demoModeActive && (!draft.id || loading || error) ? (
            <div className="chargesSettings__form"><div className="chargesSettings__alert">{demoCopy.select}</div></div>
          ) : <div className="chargesSettings__form" title={demoModeActive ? demoCopy.readOnly : undefined}>
            {error ? <div className="chargesSettings__alert chargesSettings__alert--error">{error}</div> : null}
            {notice ? <div className="chargesSettings__alert chargesSettings__alert--notice">{notice}</div> : null}

            <label className="chargesSettings__field">
              <span className="chargesSettings__label">Name</span>
              <input
                className="chargesSettings__input"
                value={draft.name}
                onChange={(event) => updateDraft("name", event.target.value)}
                disabled={!canManage || Boolean(draft.is_protected)}
                placeholder="Minibar"
              />
            </label>

            <label className="chargesSettings__field">
              <span className="chargesSettings__label">Category</span>
              <select
                className="chargesSettings__select"
                value={draft.category}
                onChange={(event) => updateDraft("category", event.target.value)}
                disabled={!canManage || Boolean(draft.is_system)}
              >
                {CATEGORY_OPTIONS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
                {demoModeActive && !CATEGORY_OPTIONS.some((option) => option.value === draft.category) && (
                  <option value={draft.category}>{draft.category || demoCopy.noValue}</option>
                )}
              </select>
            </label>

            {demoModeActive && <div className="chargesSettings__fieldGrid">
              <label className="chargesSettings__field">
                <span className="chargesSettings__label">Calculation mode</span>
                <input className="chargesSettings__input" value={draft.calculation_mode} disabled placeholder={demoCopy.noValue} />
              </label>
              <label className="chargesSettings__field">
                <span className="chargesSettings__label">Sort order</span>
                <input className="chargesSettings__input" value={draft.sort_order} disabled placeholder={demoCopy.noValue} />
              </label>
            </div>}

            <div className="chargesSettings__fieldGrid">
              <label className="chargesSettings__field">
                <span className="chargesSettings__label">Default amount</span>
                <input
                  type="number"
                  min="0"
                  step={currencyAmountStep(draft.currency || baseCurrency, currencyCatalog)}
                  inputMode={getCurrencyMinorUnits(draft.currency || baseCurrency, currencyCatalog) === 0 ? "numeric" : "decimal"}
                  className="chargesSettings__input"
                  value={draft.default_amount}
                  placeholder={demoModeActive ? demoCopy.noValue : undefined}
                  onChange={(event) => updateDraft("default_amount", event.target.value)}
                  disabled={!canManage}
                />
              </label>

              <label className="chargesSettings__field">
                <span className="chargesSettings__label">Currency</span>
                <input
                  className="chargesSettings__input"
                  value={draft.currency}
                  placeholder={demoModeActive ? demoCopy.noValue : undefined}
                  onChange={(event) => updateDraft("currency", event.target.value.toUpperCase().slice(0, 3))}
                  maxLength={3}
                  disabled={!canManage}
                />
                <small>{demoModeActive ? demoCopy.currency : <>Active charge types must use base {baseCurrency} before multi-currency can be enabled.</>}</small>
              </label>
            </div>

            <label className="chargesSettings__field">
              <span className="chargesSettings__label">Description</span>
              <textarea
                className="chargesSettings__textarea"
                value={draft.description}
                onChange={(event) => updateDraft("description", event.target.value)}
                disabled={!canManage || Boolean(draft.is_protected)}
                placeholder={demoModeActive ? demoCopy.noValue : "Operational description shown to front desk."}
              />
            </label>

            <label className="chargesSettings__switch">
              <input
                type="checkbox"
                checked={Boolean(draft.is_active)}
                onChange={(event) => updateDraft("is_active", event.target.checked)}
                disabled={!canManage || Boolean(draft.is_protected)}
              />
              Active in reservation charge workflows
            </label>

            {draft.is_protected ? (
              <div className="chargesSettings__alert">
                {demoModeActive ? demoCopy.protected : "This is a protected system charge. It can be configured, but not deleted."}
              </div>
            ) : null}

            <button
              type="button"
              className="chargesSettings__button chargesSettings__button--primary"
              onClick={saveDraft}
              disabled={saving || !canManage}
            >
              {saving ? "Saving..." : "Save charge type"}
            </button>
          </div>}
        </div>
      </section>

      {upgradeEditor ? (
        <div
          className="chargesSettings__upgradeOverlay"
          onClick={() => closeUpgradeEditor()}
        >
          <section
            className="chargesSettings__upgradeModal"
            role="dialog"
            aria-modal="true"
            aria-label="Upgrade charge matrix"
            onClick={(event) => event.stopPropagation()}
          >
            <header className="chargesSettings__upgradeHeader">
              <div>
                <p className="chargesSettings__eyebrow">Reservation charge</p>
                <h3>Upgrade matrix</h3>
              </div>
              <button
                type="button"
                className="chargesSettings__button"
                onClick={() => closeUpgradeEditor()}
                disabled={upgradeSaving}
              >
                Close
              </button>
            </header>

            <div className="chargesSettings__upgradeIntro">
              {demoModeActive && <p>{demoCopy.readOnly} · {upgradeEditor.currency}</p>}
              The hotel decides which category changes are upgrades. Leave a
              route as “Not an upgrade” when it must be treated as a normal
              reservation change. Percentage supplements use the original
              nightly price; fixed amounts are charged once per affected night.
              {upgradeError ? (
                <div className="chargesSettings__alert chargesSettings__alert--error" style={{ marginTop: "10px" }}>
                  {upgradeError}
                </div>
              ) : null}
              {!upgradeLoading && upgradeEditor.categories.length > 1 ? (
                <section
                  className="chargesSettings__upgradeOrder"
                  aria-label="Room category display order"
                >
                  <div className="chargesSettings__upgradeOrderHeader">
                    <span className="chargesSettings__upgradeOrderTitle">
                      Category order
                    </span>
                    <span className="chargesSettings__upgradeOrderHint">
                      {demoModeActive ? demoCopy.order : "Drag or use the arrows · applies to rows and columns"}
                    </span>
                  </div>
                  <div className="chargesSettings__upgradeOrderList">
                    {upgradeEditor.categories.map((category, categoryIndex) => (
                      <div
                        className={`chargesSettings__upgradeOrderItem ${
                          Number(draggedUpgradeCategoryId) === Number(category.id)
                            ? "chargesSettings__upgradeOrderItem--dragging"
                            : ""
                        }`}
                        key={category.id}
                        draggable={!demoModeActive && !upgradeSaving}
                        onDragStart={(event) => {
                          if (demoModeActive || isDemoMode()) { event.preventDefault(); return; }
                          event.dataTransfer.effectAllowed = "move";
                          event.dataTransfer.setData(
                            "text/plain",
                            String(category.id)
                          );
                          setDraggedUpgradeCategoryId(Number(category.id));
                        }}
                        onDragEnd={() => { if (!demoModeActive && !isDemoMode()) setDraggedUpgradeCategoryId(null); }}
                        onDragOver={(event) => {
                          if (demoModeActive || isDemoMode()) return;
                          event.preventDefault();
                          event.dataTransfer.dropEffect = "move";
                        }}
                        onDrop={(event) => {
                          if (demoModeActive || isDemoMode()) { event.preventDefault(); return; }
                          event.preventDefault();
                          const sourceCategoryId = Number(
                            event.dataTransfer.getData("text/plain") ||
                              draggedUpgradeCategoryId
                          );
                          reorderUpgradeCategories(
                            sourceCategoryId,
                            category.id
                          );
                          setDraggedUpgradeCategoryId(null);
                        }}
                        title={demoModeActive ? demoCopy.order : category.name}
                        style={demoModeActive ? { cursor: "default" } : undefined}
                      >
                        <span
                          className="chargesSettings__upgradeOrderHandle"
                          aria-hidden="true"
                        >
                          ⋮⋮
                        </span>
                        <span className="chargesSettings__upgradeOrderPosition">
                          {categoryIndex + 1}
                        </span>
                        <span className="chargesSettings__upgradeOrderName">
                          {category.name}
                        </span>
                        <span className="chargesSettings__upgradeOrderActions">
                          <button
                            type="button"
                            className="chargesSettings__upgradeOrderButton"
                            aria-label={`Move ${category.name} one position left`}
                            disabled={categoryIndex === 0 || upgradeSaving || !canManage}
                            onClick={() => moveUpgradeCategory(category.id, -1)}
                          >
                            ←
                          </button>
                          <button
                            type="button"
                            className="chargesSettings__upgradeOrderButton"
                            aria-label={`Move ${category.name} one position right`}
                            disabled={
                              categoryIndex ===
                                upgradeEditor.categories.length - 1 ||
                              upgradeSaving ||
                              !canManage
                            }
                            onClick={() => moveUpgradeCategory(category.id, 1)}
                          >
                            →
                          </button>
                        </span>
                      </div>
                    ))}
                  </div>
                </section>
              ) : null}
            </div>

            <div className="chargesSettings__upgradeMatrixWrap">
              {upgradeLoading ? (
                <div className="chargesSettings__alert">
                  Loading room categories and upgrade rules...
                </div>
              ) : upgradeEditor.categories.length ? (
                <table
                  className="chargesSettings__upgradeMatrix"
                  style={{
                    width: `${
                      (upgradeEditor.categories.length + 1) *
                      UPGRADE_MATRIX_COLUMN_WIDTH
                    }px`,
                  }}
                >
                  <colgroup>
                    {Array.from(
                      { length: upgradeEditor.categories.length + 1 },
                      (_, columnIndex) => (
                        <col
                          key={columnIndex}
                          style={{ width: `${UPGRADE_MATRIX_COLUMN_WIDTH}px` }}
                        />
                      )
                    )}
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="chargesSettings__upgradeCorner">
                        <span>FROM</span>
                        Category → TO
                      </th>
                      {upgradeEditor.categories.map((toCategory) => (
                        <th key={toCategory.id} scope="col">
                          {toCategory.name}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {upgradeEditor.categories.map((fromCategory) => (
                      <tr key={fromCategory.id}>
                        <th scope="row">{fromCategory.name}</th>
                        {upgradeEditor.categories.map((toCategory) => {
                          if (Number(fromCategory.id) === Number(toCategory.id)) {
                            return (
                              <td
                                className="chargesSettings__upgradeSame"
                                key={toCategory.id}
                              >
                                Same category
                              </td>
                            );
                          }

                          const key = upgradeRuleKey(
                            fromCategory.id,
                            toCategory.id
                          );
                          const rule = upgradeRules[key] || null;
                          const calculationMode =
                            rule?.calculation_mode || (demoModeActive ? "" : "percentage");
                          return (
                            <td key={toCategory.id}>
                              <div className="chargesSettings__upgradeCell">
                                <div className="chargesSettings__upgradeValue">
                                  <input
                                    type="number"
                                    inputMode={
                                      calculationMode === "fixed_amount" &&
                                      getCurrencyMinorUnits(upgradeEditor.currency, currencyCatalog) === 0
                                        ? "numeric"
                                        : "decimal"
                                    }
                                    min={calculationMode === "fixed_amount" ? currencyAmountStep(upgradeEditor.currency, currencyCatalog) : "0.01"}
                                    step={calculationMode === "fixed_amount" ? currencyAmountStep(upgradeEditor.currency, currencyCatalog) : "0.01"}
                                    value={rule?.value || ""}
                                    placeholder="—"
                                    disabled={!canManage}
                                    aria-label={`Supplement from ${fromCategory.name} to ${toCategory.name}`}
                                    onChange={(event) =>
                                      updateUpgradeRuleValue(
                                        fromCategory.id,
                                        toCategory.id,
                                        event.target.value
                                      )
                                    }
                                  />
                                  <div
                                    className="chargesSettings__upgradeModeChips"
                                    role="group"
                                    aria-label={`Supplement type from ${fromCategory.name} to ${toCategory.name}`}
                                  >
                                    <button
                                      type="button"
                                      className={`chargesSettings__upgradeModeChip ${
                                        calculationMode === "percentage"
                                          ? "chargesSettings__upgradeModeChip--selected"
                                          : ""
                                      }`}
                                      aria-label="Percentage per night"
                                      aria-pressed={
                                        calculationMode === "percentage"
                                      }
                                      disabled={!canManage}
                                      onClick={() =>
                                        updateUpgradeRuleMode(
                                          fromCategory.id,
                                          toCategory.id,
                                          "percentage"
                                        )
                                      }
                                    >
                                      %
                                    </button>
                                    <button
                                      type="button"
                                      className={`chargesSettings__upgradeModeChip ${
                                        calculationMode === "fixed_amount"
                                          ? "chargesSettings__upgradeModeChip--selected"
                                          : ""
                                      }`}
                                      aria-label={`Fixed amount per night in ${upgradeEditor.currency}`}
                                      aria-pressed={
                                        calculationMode === "fixed_amount"
                                      }
                                      disabled={!canManage}
                                      onClick={() =>
                                        updateUpgradeRuleMode(
                                          fromCategory.id,
                                          toCategory.id,
                                          "fixed_amount"
                                        )
                                      }
                                    >
                                      {currencySymbol(upgradeEditor.currency)}
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <div className="chargesSettings__alert">
                  This hotel has no room categories to configure.
                </div>
              )}
            </div>

            <footer className="chargesSettings__upgradeFooter">
              <div className="chargesSettings__upgradeLegend">
                {configuredUpgradeRuleCount} upgrade route
                {configuredUpgradeRuleCount === 1 ? "" : "s"} configured ·
                Blank routes remain normal category changes.
              </div>
              <div className="chargesSettings__upgradeFooterActions">
                <button
                  type="button"
                  className="chargesSettings__button"
                  onClick={() => closeUpgradeEditor()}
                  disabled={upgradeSaving}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="chargesSettings__button chargesSettings__button--primary"
                  onClick={saveUpgradeRules}
                  disabled={upgradeLoading || upgradeSaving || !canManage}
                >
                  {upgradeSaving ? "Saving..." : "Save upgrade matrix"}
                </button>
              </div>
            </footer>
          </section>
        </div>
      ) : null}
    </div>
  );
}
